




















































































import { Vue } from 'vue-property-decorator';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { Action, Modules } from '@/store/store';
import { ARIA_REFS, getMultiRefsStringified } from '@/config/accessibility';
import SubHeadline from '../sub-headline/sub-headline.vue';
import BatteryCellLayers from './battery-cell-layers.vue';
import BatteryLabel from './battery-label.vue';
import BatteryMileage from '../mileage/battery-mileage.vue';
import { BoundingBox } from '@/store/modules/config';
import { SubBrand } from '@/store/modules/themes';

const chargingDataModule = namespace(Modules.chargingData);
const configModule = namespace(Modules.config);
const themeModule = namespace(Modules.themes);

@Component({
  name: 'eqweb-battery',
  components: { SubHeadline, BatteryLabel, BatteryMileage, BatteryCellLayers },
})
export default class Battery extends Vue {
  $refs!: {
    borderImg: HTMLDivElement;
  };
  private lastHandleChange?: 'chargingValue' | 'chargingValueStart' = undefined;

  @chargingDataModule.State('chargingValue')
  private readonly chargingValue!: number;

  @chargingDataModule.State('chargingValueStart')
  private readonly chargingValueStart!: number;

  @chargingDataModule.Action('setBatteryCharging')
  private readonly setBatteryCharging!: Action<number>;

  @chargingDataModule.Action('setBatteryChargingStart')
  private readonly setBatteryChargingStart!: Action<number>;

  @configModule.Getter('asSidebar')
  private readonly asSidebar!: boolean;

  @configModule.Getter('appBoundingBox')
  private readonly appBoundingBox!: BoundingBox;

  @themeModule.Getter('subBrand')
  private readonly subBrand!: SubBrand;

  /* true = using double slider for battery value */
  @configModule.Getter('hasBatteryStartSlider')
  private readonly hasBatteryStartSlider!: boolean;

  @configModule.Getter('hasBatteryMileage')
  private readonly hasBatteryMileage!: boolean;

  private borderImageRef() {
    return this.$refs.borderImg;
  }

  // ⚠️ This function stop bubbling the wbchange event outside of this component
  // This was a huge bug we dealt with while using the CTC inside the WCC (Web Component Collection)
  private stopPropagatingToWCC(e: Event) {
    e.preventDefault();
    e.stopPropagation();
  }

  private updateChargingValueStart(e: Event): void {
    const startVal = parseInt((e.target as HTMLInputElement).value);
    this.setBatteryChargingStart(startVal);
    if (startVal > this.chargingValue) {
      this.setBatteryCharging(startVal);
    }
    this.lastHandleChange = 'chargingValueStart';
  }

  private updateChargingValue(e: Event): void {
    const val = parseInt((e.target as HTMLInputElement).value);
    this.setBatteryCharging(val);
    if (val < this.chargingValueStart) {
      this.setBatteryChargingStart(val);
    }
    this.lastHandleChange = 'chargingValue';
  }

  get warningText(): string {
    if (this.chargingValue <= 80) {
      return this.$t('BATTERY.INFOTEXT.UNDER_80') as string;
    }
    return this.$t('BATTERY.INFOTEXT.OVER_80') as string;
  }

  get showWarning(): boolean {
    return this.warningText?.length > 0;
  }

  private setToEmpty() {
    if (this.hasBatteryStartSlider) {
      this.setBatteryChargingStart(0);
    }

    this.setBatteryCharging(0);
  }

  private setToFull() {
    this.setBatteryCharging(100);
  }

  private get cssVariables() {
    if (this.asSidebar) {
      const padding = 12;
      const batteryWidth = this.appBoundingBox?.width ? this.appBoundingBox.width - padding : 200;
      return {
        '--battery-range-width': batteryWidth + 'px',
      };
    }
    return '';
  }

  private get ARIA_REFS() {
    return ARIA_REFS;
  }

  private get inputAriaLabeledBy() {
    return getMultiRefsStringified(
      ARIA_REFS.BATTERY_HEADLINE,
      ARIA_REFS.BATTERY_CHARGER_LABEL,
      ARIA_REFS.BATTERY_CHARGING_OPTIONS,
      ARIA_REFS.BATTERY_MILEAGE,
      ARIA_REFS.BATTERY_INFOTEXT,
    );
  }
}
